if (!customElements.get('donation-module')) {
  class DonationModule extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;


      // Get product and variant IDs
      this.productId = parseInt(this.dataset.productId, 10) || null;
      this.variantId = parseInt(this.dataset.variantId, 10) || null;
      this.activeVariantId = parseInt(this.dataset.activeVariantId, 10) || null;
      if (!this.productId || !this.variantId ) {
        console.log(
          'donation-module: missing data-product-id or data-variant-id. Check donation_product theme setting.'
        );
        return;
      }

      // Selectors
      this.cartItems = document.querySelector('cart-items');

      // Event listeners
      this.addEventListener('click', this.handleClick.bind(this), { signal });

      this.querySelectorAll('[data-donation-custom-input]').forEach((input) => {
        input.addEventListener('blur', () => this.onCustomBlur(input), { signal });
        // Enter should apply the amount instead of submitting the surrounding form.
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            input.blur();
          }
        }, { signal });
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleClick(e) {
      if (e.target.closest('[data-donation-yes]')) return this.onYes();
      if (e.target.closest('[data-donation-no]')) return this.onNo();
      const amountBtn = e.target.closest('[data-donation-amount]');
      if (amountBtn) return this.onSelectAmount(amountBtn);
      if (e.target.closest('[data-donation-custom-btn]')) return this.onSelectCustom();
    }

    onYes() {
      this.querySelector('[data-donation-yes]')?.classList.add('is-active');
      this.querySelector('[data-donation-no]')?.classList.remove('is-active');
      const options = this.querySelector('[data-donation-options]');
      if (options) options.hidden = false;

      // Drop the persisted decline once the shopper opts back in.
      if (this.dataset.declined === 'true') this.clearDeclined();
    }

    applyDeclinedState() {
      this.querySelector('[data-donation-no]')?.classList.add('is-active');
      this.querySelector('[data-donation-yes]')?.classList.remove('is-active');
      const options = this.querySelector('[data-donation-options]');
      if (options) options.hidden = true;
    }

    onNo() {
      this.applyDeclinedState();
      this.resetSelection();
      this.hideWarning();
      this.declineDonation();
    }

    // Selecting a fixed amount adds it to the cart straight away.
    onSelectAmount(btn) {
      if (this.classList.contains('is-loading')) return;

      const variantId = parseInt(btn.dataset.donationVariantId, 10) || null;
      const cents = parseInt(btn.dataset.donationAmount, 10);

      // Ignore re-clicks on the amount that is already in the cart.
      if (
        this.dataset.isDonationInCart === 'true'
        && variantId === this.activeVariantId
        && cents === parseInt(this.dataset.currentAmount, 10)
      ) return;

      this.querySelectorAll('.donation__amount-btn').forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      const customWrap = this.querySelector('[data-donation-custom-wrap]');
      if (customWrap) customWrap.hidden = true;

      this.addSelectedDonation();
    }

    // The custom option only reveals the input; the amount is added on blur/Enter.
    onSelectCustom() {
      if (!this.variantId) {
        console.error(
          'donation-module: missing data-variant-id for custom donation. ' +
          'Set custom.donation on the donation product variant.'
        );
        return;
      }

      this.querySelectorAll('.donation__amount-btn').forEach((b) => b.classList.remove('is-selected'));
      this.querySelector('[data-donation-custom-btn]')?.classList.add('is-selected');

      const customWrap = this.querySelector('[data-donation-custom-wrap]');
      if (customWrap) customWrap.hidden = false;

      this.querySelector('[data-donation-custom-input]')?.focus();
    }

    onCustomBlur(input) {
      const val = parseFloat(input.value.replace(',', '.'));
      if (!Number.isFinite(val) || val <= 0) return;

      const cents = Math.round(val * 100);
      input.value = (cents / 100).toFixed(2);

      // Ignore blur when the same custom amount is already in the cart.
      if (
        this.dataset.isDonationInCart === 'true'
        && this.activeVariantId === this.variantId
        && cents === parseInt(this.dataset.currentAmount, 10)
      ) return;

      this.addSelectedDonation();
    }

    resetSelection() {
      this.querySelectorAll('.donation__amount-btn').forEach((b) => b.classList.remove('is-selected'));
      const customWrap = this.querySelector('[data-donation-custom-wrap]');
      if (customWrap) customWrap.hidden = true;
      const customInput = this.querySelector('[data-donation-custom-input]');
      if (customInput) customInput.value = '';
    }

    // True once the shopper made an explicit choice: donation in cart or "Not today".
    hasMadeChoice() {
      return this.dataset.isDonationInCart === 'true' || this.dataset.declined === 'true';
    }

    showWarning() {
      const warning = this.querySelector('[data-donation-warning]');
      if (warning) warning.hidden = false;
      this.classList.add('has-warning');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Also surface the warning next to the checkout button in the cart
      // drawer footer, which can be out of view from the module itself.
      const footerWarning = document.querySelector('[data-donation-warning-footer]');
      if (footerWarning) footerWarning.hidden = false;
    }

    hideWarning() {
      const warning = this.querySelector('[data-donation-warning]');
      if (warning) warning.hidden = true;
      this.classList.remove('has-warning');

      const footerWarning = document.querySelector('[data-donation-warning-footer]');
      if (footerWarning) footerWarning.hidden = true;
    }

    async addSelectedDonation() {
      const selectedAmount = this.getSelectedAmount();
      if (!selectedAmount) return;

      const selectedBtn = this.querySelector('.donation__amount-btn.is-selected');
      const isCustomSelection = selectedBtn?.hasAttribute('data-donation-custom-btn');
      const selectedVariantId = parseInt(selectedBtn?.dataset.donationVariantId, 10) || null;
      const variantId = selectedVariantId || (isCustomSelection ? this.variantId : null);

      if (!variantId) {
        console.error('donation-module: invalid variant ID. Check donation_product theme setting.');
        return;
      }

      this.setLoading(true);

      try {
        const removed = await this.removeDonation({ silent: true });
        if (!removed) {
          throw new Error('donation-module: failed to remove existing donation before re-adding');
        }

        const body = JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              _donation_price: selectedAmount,
              _donation_type: 'free-amount-cart',
            },
          }],
          sections: this.getSectionsToRender().map((s) => s.section).filter(Boolean),
          sections_url: window.location.pathname,
        });

        const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
        const parsedState = await response.json();

        if (parsedState.status === 422) {
          throw new Error(parsedState.description || 'Unable to add donation');
        }

        this.renderSections(parsedState.sections);
        this.dataset.isDonationInCart = 'true';
        this.dataset.currentAmount = String(selectedAmount);
        this.dataset.activeVariantId = String(variantId);
        this.activeVariantId = variantId;

        // /cart/add returns the added line item — capture its key for later removal
        const addedKey = parsedState.key || parsedState.items?.[0]?.key;
        if (addedKey) this.dataset.lineKey = addedKey;

        this.hideWarning();
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    getSelectedAmount() {
      const fixedBtn = this.querySelector('.donation__amount-btn.is-selected:not([data-donation-custom-btn])');
      if (fixedBtn) return parseInt(fixedBtn.dataset.donationAmount, 10);

      const customBtn = this.querySelector('.donation__amount-btn.is-selected[data-donation-custom-btn]');
      if (customBtn) {
        const input = this.querySelector('[data-donation-custom-input]');
        const val = parseFloat(input?.value?.replace(',', '.') || '0');
        return val > 0 ? Math.round(val * 100) : 0;
      }

      return 0;
    }

    // Persist the "Not today" choice as a cart attribute and drop any existing
    // donation line in a single /cart/update. donation-module.liquid reads
    // cart.attributes.no_cart_donation to render the declined state when the
    // sections re-render (e.g. the cart drawer rebuilds the whole module).
    async declineDonation() {
      const sections = this.getSectionsToRender(false);
      const updates = {};
      const id = this.dataset.lineKey || this.activeVariantId;
      if (this.dataset.isDonationInCart === 'true' && id) {
        updates[id] = 0;
      }

      this.setLoading(true);
      try {
        const body = JSON.stringify({
          attributes: { no_cart_donation: 'true' },
          updates,
          sections: sections.map((s) => s.section).filter(Boolean),
          sections_url: window.location.pathname,
        });

        const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
        if (!response.ok) return;

        const parsedState = await response.json();

        this.dataset.isDonationInCart = 'false';
        this.dataset.currentAmount = '0';
        this.dataset.activeVariantId = '';
        this.dataset.lineKey = '';
        this.dataset.declined = 'true';
        this.activeVariantId = null;

        this.renderSections(parsedState.sections, sections);
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    // Clear the persisted decline attribute when the shopper opts back in.
    async clearDeclined() {
      this.dataset.declined = 'false';
      try {
        await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes: { no_cart_donation: '' } }),
        });
      } catch (error) {
        console.error(error);
      }
    }

    async removeDonation({ rerenderModule = false, silent = false } = {}) {
      const hasDonation = this.dataset.isDonationInCart === 'true'
        || !!this.dataset.lineKey
        || !!this.activeVariantId;
      if (!hasDonation) return true;

      const lineKey = this.dataset.lineKey;
      const variantId = this.activeVariantId || this.variantId;
      const id = lineKey || variantId;
      if (!id) return true;

      try {
        const sections = rerenderModule ? this.getSectionsToRender(true) : this.getSectionsToRender(false);
        const body = JSON.stringify({
          id,
          quantity: 0,
          sections: sections.map((s) => s.section).filter(Boolean),
          sections_url: window.location.pathname,
        });

        const response = await fetch(routes.cart_change_url, { ...fetchConfig(), body });
        if (!response.ok) return false;

        const parsedState = await response.json();
        if (!silent) this.renderSections(parsedState.sections, sections);

        this.dataset.isDonationInCart = 'false';
        this.dataset.currentAmount = '0';
        this.dataset.activeVariantId = '';
        this.dataset.lineKey = '';
        this.activeVariantId = null;
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    getSectionsToRender(includeModule = false) {
      const sharedSections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];

      if (document.querySelector('cart-drawer')) {
        const sections = [
          {
            id: 'cart-drawer',
            section: 'cart-drawer',
            selector: '[data-cart-drawer]',
          },
          ...sharedSections,
        ];

        if (includeModule) {
          const moduleEl = document.getElementById('donation-module');
          if (moduleEl) {
            sections.push({
              id: 'donation-module',
              section: moduleEl.dataset.id,
              selector: '[data-donation-module-content]',
            });
          }
        }

        return sections;
      }

      const sections = [];

      const cartItems = document.getElementById('main-cart-items');
      if (cartItems?.dataset?.id) {
        sections.push({
          id: 'main-cart-items',
          section: cartItems.dataset.id,
          selector: '.js-cart-item-contents',
        });
      }

      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter?.dataset?.id) {
        sections.push({
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '.js-cart-footer-contents',
        });
      }

      if (includeModule) {
        const moduleEl = document.getElementById('donation-module');
        if (moduleEl) {
          sections.push({
            id: 'donation-module',
            section: moduleEl.dataset.id,
            selector: '[data-donation-module-content]',
          });
        }
      }

      return sections.concat(sharedSections);
    }

    renderSections(sections, sectionsToRender) {
      const toRender = sectionsToRender || this.getSectionsToRender(false);
      if (!sections) return;

      const cartDrawer = document.querySelector('cart-drawer');
      const cartItems = document.querySelector('cart-drawer cart-items') || document.querySelector('cart-items');
      cartItems?.rememberPolicyState?.();

      toRender.forEach((item) => {
        if (item.id === 'cart-drawer') return;

        const el = document.getElementById(item.id);
        if (!el || !sections[item.section]) return;
        const target = el.querySelector(item.selector) || el;
        const parsed = new DOMParser().parseFromString(sections[item.section], 'text/html');
        const inner = parsed.querySelector(item.selector);
        if (inner) target.innerHTML = inner.innerHTML;
      });

      if (cartDrawer && sections['cart-drawer']) {
        cartDrawer.renderContents({ sections });
      }

      this.cartItems = document.querySelector('cart-drawer cart-items') || document.querySelector('cart-items');
      this.cartItems?.applyPolicyState?.();
      this.cartItems?.syncCheckoutButton?.();
    }

    setLoading(loading) {
      // cart.js checks this class to postpone a checkout submit while updating.
      this.classList.toggle('is-loading', loading);
      if (loading) {
        this.cartItems?.enableLoading?.();
      } else {
        this.cartItems?.disableLoading?.();
        this.cartItems?.submitFormButtonHandler?.();
      }
    }
  }

  window.DonationModule = DonationModule;
  customElements.define('donation-module', DonationModule);
}
